import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
  TripCategory,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FilterTime,
  FilterFlightNumber,
  useFilterDialog,
} from "@tenant/flights";
import {
  FilterDialogContainer,
  FilterDialogSection,
  FilterPrice,
} from "@tenant/ui";
import clsx from "clsx";
import { FilterAirline } from "../FilterAirline";
import { FilterFareClass } from "../FilterFareClass";
import { FilterStops } from "../FilterStops";
import defaultStyles from "./FilterDialog.module.scss";

export interface FilterDialogComponentProps extends PlatformComponentProps {
  sliceIndex: number;
  applyAirlineFiltersToFlightNumber?: boolean;
}
export const FilterDialog = ({
  sliceIndex,
  children,
  className,
  applyAirlineFiltersToFlightNumber,
}: FilterDialogComponentProps) => {
  const styles = useFlightStyles(
    CoreFlightsComponent.FilterDialog,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FilterDialog);
  const { t } = useI18nContext();

  const {
    context: {
      fareClasses,
      stop,
      filterOptions,
      airline,
      origin,
      destination,
      outboundDepartureTimeRange,
      outboundArrivalTimeRange,
      returnDepartureTimeRange,
      returnArrivalTimeRange,
      tripCategory,
      priceState,
      flightNumbersByAirline,
      flightNumber,
    },
    handlers: {
      handleFareClassesChange,
      handleAirlineChange,
      handleOutboundTimeRangeChange,
      handleReturnTimeRangeChange,
      handlePriceChange,
      handleFlightNumberChange,
      handleStopsChange,
      updateValuesToMatchState,
      resetAllFilters,
      applyFilter,
    },
  } = useFilterDialog({ sliceIndex, applyAirlineFiltersToFlightNumber });

  return (
    <FilterDialogContainer
      onClickDialogTrigger={updateValuesToMatchState}
      onClickReset={resetAllFilters}
      onClickApply={applyFilter}
      className={clsx(className, block)}
    >
      <FilterDialogSection
        title={t("searchFilter.fareClass")}
        content={
          <FilterFareClass
            fareClasses={fareClasses}
            onFareClassChange={handleFareClassesChange}
          />
        }
      />
      <FilterDialogSection
        title={t("searchFilter.stops")}
        content={
          <FilterStops value={stop} onStopFilterChange={handleStopsChange} />
        }
      />
      <FilterDialogSection
        title={t("searchFilter.airline")}
        content={
          <FilterAirline
            allAirlines={filterOptions.airlineOptions}
            airlineFilter={airline}
            onAirlineFilterChange={handleAirlineChange}
          />
        }
      />
      {sliceIndex === 0 ? (
        <FilterDialogSection
          title={t("core-flights.searchFilter.outboundTimes")}
          content={
            <FilterTime
              origin={origin}
              destination={destination}
              departureTimeRange={outboundDepartureTimeRange}
              arrivalTimeRange={outboundArrivalTimeRange}
              onSliceTimeRangeChange={handleOutboundTimeRangeChange}
            />
          }
        />
      ) : null}
      {tripCategory === TripCategory.ROUND_TRIP ? (
        <FilterDialogSection
          title={t("core-flights.searchFilter.returnTimes")}
          content={
            <FilterTime
              origin={destination}
              destination={origin}
              departureTimeRange={returnDepartureTimeRange}
              arrivalTimeRange={returnArrivalTimeRange}
              onSliceTimeRangeChange={handleReturnTimeRangeChange}
            />
          }
        />
      ) : null}
      <FilterDialogSection
        title={t("searchFilter.maxPrice")}
        className={cn("price")}
        content={
          <FilterPrice
            className={cn("filter-popover")}
            label={t("searchFilter.maxPrice")}
            minimumPrice={filterOptions.priceMin}
            maximumPrice={filterOptions.priceMax}
            initialValue={{
              minPrice: filterOptions.priceMin?.value ?? 0,
              maxPrice: priceState,
            }}
            onFilterPriceChange={handlePriceChange}
          />
        }
      />
      {/* TODO: fix */}
      {/* <FilterDialogSection
          title={t("searchFilter.airport")}
          content={
            <FilterAirport
              allAirports={filterOptions.airportOptions}
              airportFilter={airport}
              onAirportFilterChange={handleAirportChange}
            />
          }
        /> */}
      <FilterDialogSection
        title={t("searchFilter.flightNumber")}
        content={
          <FilterFlightNumber
            flightNumbersByAirline={flightNumbersByAirline}
            onFlightNumberChange={handleFlightNumberChange}
            selectedFlightNumber={flightNumber}
          />
        }
      />
      {children}
    </FilterDialogContainer>
  );
};
